import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { useT } from "../lib/i18n";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

interface LoginScreenProps {
  onSwitchToSignup(): void;
  onForgotPassword?(): void;
}

const GOOGLE_ENABLED = import.meta.env.VITE_GOOGLE_ENABLED === "true";

export function LoginScreen({
  onSwitchToSignup,
  onForgotPassword,
}: LoginScreenProps): JSX.Element {
  const [, T] = useT();
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const next = new URLSearchParams(location.search).get("next") ?? "/today";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      void navigate(next, { replace: true });
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === "auth.account_deactivated"
      ) {
        setError(T("auth.accountDeactivated"));
      } else {
        setError(
          caught instanceof ApiError ? caught.message : T("somethingWentWrong"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <span>{T("noAccountYet")}</span>
      <button type="button" className="vz-link" onClick={onSwitchToSignup}>
        {T("signUp")}
      </button>
    </>
  );

  return (
    <AuthShell title={T("signIn")} footer={footer} ariaLabel={T("signIn")}>
      <form
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div className="vz-auth-field">
          <label htmlFor="login-email">{T("email")}</label>
          <input
            id="login-email"
            className="vz-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="vz-auth-field">
          <label htmlFor="login-password">{T("password")}</label>
          <input
            id="login-password"
            className="vz-input"
            type="password"
            autoComplete="current-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error !== null && (
          <div className="vz-error" role="alert">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="vz-btn vz-btn-primary"
          disabled={submitting}
          style={{ justifyContent: "center", padding: "10px" }}
        >
          {submitting ? T("loading") : T("signIn")}
        </button>
        {GOOGLE_ENABLED && (
          <a
            className="vz-btn vz-btn-ghost"
            href="/api/auth/google/start?intent=auth"
            style={{ justifyContent: "center", padding: "10px" }}
          >
            {T("signInWithGoogle")}
          </a>
        )}
        {onForgotPassword !== undefined && (
          <button
            type="button"
            className="vz-link"
            onClick={onForgotPassword}
            style={{
              alignSelf: "flex-start",
              background: "transparent",
              border: 0,
              padding: 0,
              color: "var(--accent)",
              fontSize: 12,
            }}
          >
            {T("forgotPassword")}
          </button>
        )}
      </form>
    </AuthShell>
  );
}
